import { ChartHeader } from "@/components/ChartHeader";
import { NoData } from "@/components/NoData";
import { Select } from "@/components/Select";
import { formatMoney } from "@/utils/format";
import { getEconomyDisplayName, formatPercentage } from "@/utils/format";
import { getProductName } from "../utils";
import type { SuppliersDataRow } from "../App";
import styles from "./styles.module.scss";

interface SuppliersChartProps {
  data: SuppliersDataRow[];
  displayEconomy: string;
  product: string;
  productOptions: string[];
  onProductChange: (newValue: string) => void;
}

export function SuppliersChart({
  data,
  displayEconomy,
  product,
  productOptions,
  onProductChange,
}: SuppliersChartProps) {
  const filteredData = data.sort((a, b) => b.Value - a.Value).slice(0, 10);

  const maxValue = Math.max(...filteredData.map((row) => row.Value));

  const iterator = Array.from(Array(filteredData.length).keys());

  const titleId = "product-concentration-suppliers-title";

  return (
    <div className={styles.wrap}>
      <ChartHeader
        title={`Top suppliers of selected product to ${displayEconomy}`}
        titleId={titleId}
        description={`${getProductName(
          product
        )?.toLowerCase()} (HS6 code: ${product})`}
      >
        <Select
          label="Product"
          items={productOptions.map((o) => ({
            value: o,
            label: getProductName(o, 60),
          }))}
          value={product}
          onChange={(value) => {
            onProductChange(value);
          }}
        />
      </ChartHeader>

      {!filteredData.length ? (
        <NoData />
      ) : (
        <>
          <table className={styles.table} aria-labelledby={titleId}>
            <thead>
              <tr>
                <th>Economy</th>
                <th>Value, $</th>
                <th>
                  Share of
                  <br />
                  total imports, %
                </th>
              </tr>
            </thead>
            <tbody>
              {iterator.map((_, index) => {
                const row = filteredData[index];

                return (
                  <tr key={index}>
                    <td className={styles["economy-col"]}>
                      <p className={styles["economy-col-content"]}>
                        <span className={styles.index} aria-hidden>
                          {index + 1}
                        </span>{" "}
                        <span className={styles["economy-col-text"]}>
                          {getEconomyDisplayName(row["Partner code"])}
                        </span>
                      </p>
                    </td>
                    <td className={styles["value-col"]}>
                      <div className={styles["value-col-container"]}>
                        <div className={styles["value-bar-container"]}>
                          <div
                            className={styles["value-bar"]}
                            style={
                              {
                                "--width": Math.round(
                                  (row.Value / maxValue) * 100
                                ),
                              } as React.CSSProperties
                            }
                          />
                        </div>
                        <p className={styles["value-text"]}>
                          {formatMoney(row.Value)}
                        </p>
                      </div>
                    </td>
                    <td className={styles["percent-col"]}>
                      {formatPercentage(row["Share of total imports"] / 100)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className={styles.list}>
            {iterator.map((_, index) => {
              const row = filteredData[index];

              return (
                <div key={index} className={styles["list-row"]}>
                  <p className={styles["row-title"]}>
                    <span className={styles.index}>{index + 1}</span>
                    <span className={styles["economy-row"]}>
                      {getEconomyDisplayName(row["Partner code"])}
                    </span>
                  </p>
                  <div className={styles["value-row"]}>
                    <p className={styles["value-label"]}>Export value</p>
                    <div className={styles["value-col-container"]}>
                      <div className={styles["value-bar-container"]}>
                        <div
                          className={styles["value-bar"]}
                          style={
                            {
                              "--width": Math.round(
                                (row.Value / maxValue) * 100
                              ),
                            } as React.CSSProperties
                          }
                        />
                      </div>
                      <p className={styles["value-text"]}>
                        {formatMoney(row.Value)}
                      </p>
                    </div>
                  </div>
                  <p className={styles["percent-row"]}>
                    {formatPercentage(row["Share of total imports"] / 100)} of
                    total imports
                  </p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
